import { GQL } from "@binale-tech/shared";

import type { MessageId } from "@domain/intl/useTranslation";

import { readAgentMode, writeAgentMode } from "../agentModeStorage";
import { withThinking } from "../runner/runAgent";
import type { AgentChip, AgentStep } from "../runner/types";

const STALE_BANK_DAYS = 30;
const DAY_MS = 86_400_000;

export const startStep: AgentStep = {
    id: "start",
    async run(ctx, io) {
        const [buckets, documents, mailbox] = await withThinking(io, "app.agent.start.thinking", () =>
            Promise.all([
                ctx.readPendingBuckets(),
                ctx.canRead(GQL.IProductKey.Dms) ? ctx.countNewDocuments() : Promise.resolve(null),
                ctx.readMailboxConfig().catch((error: unknown): null => {
                    console.error("Agent start: mailbox read failed", error);
                    return null;
                }),
            ]),
        );
        // The first read of the run is the "before"; a later start never replaces it.
        ctx.runReport.baseline ??= buckets;
        const connected = ctx.banks().filter((b) => b.finapiBankConnectionId != null);
        const sum = (kind: GQL.IPendingBucketKind) =>
            buckets.filter((b) => b.kind === kind).reduce((total, b) => total + b.count, 0);
        const chips: AgentChip[] = [];
        if (documents !== null) {
            chips.push({ textId: "app.agent.start.chip.documents", values: { count: documents } });
        }
        chips.push({ textId: "app.agent.start.chip.banks", values: { count: connected.length } });
        if (mailbox?.connectionStatus === GQL.IMailboxConnectionStatus.Ok) {
            chips.push({ textId: "app.agent.start.chip.mailboxOk", tone: "ok" });
        } else if (mailbox?.connectionStatus === GQL.IMailboxConnectionStatus.Error) {
            chips.push({ textId: "app.agent.start.chip.mailboxError", tone: "warn" });
        }
        const open: [MessageId, number][] = [
            ["app.agent.start.chip.openDocuments", sum(GQL.IPendingBucketKind.DocumentsReadyToBook)],
            ["app.agent.start.chip.openBank", sum(GQL.IPendingBucketKind.BankTransactionsNotBooked)],
            ["app.agent.start.chip.openKasse", sum(GQL.IPendingBucketKind.KasseTransactionsNotBooked)],
        ];
        for (const [textId, count] of open) {
            if (count > 0) chips.push({ textId, values: { count }, tone: "accent" });
        }
        io.post({ kind: "info", textId: "app.agent.start.found", values: { year: ctx.year }, chips });

        if (mailbox?.connectionStatus === GQL.IMailboxConnectionStatus.Error) {
            io.post({ kind: "warning", textId: "app.agent.start.mailboxError" });
        }
        const today = new Date().toISOString().slice(0, 10);
        for (const bank of connected) {
            if (!bank.finapiImportDate) continue;
            const ageDays = (Date.parse(today) - Date.parse(bank.finapiImportDate.slice(0, 10))) / DAY_MS;
            if (ageDays > STALE_BANK_DAYS) {
                io.post({ kind: "warning", textId: "app.agent.start.bankStale", values: { bank: bank.bankName } });
            }
        }

        // The mode is chosen before any question or write.
        await io.panel({
            type: "modeChoice",
            initial: readAgentMode(ctx.companyId),
            onChosen: (mode) => {
                writeAgentMode(ctx.companyId, mode);
                io.setMode(mode);
            },
        });

        if (ctx.canWrite(GQL.IProductKey.Dms)) {
            const answer = await io.ask("app.agent.start.uploadQuestion", [
                { value: "upload", labelId: "app.agent.start.upload", primary: true },
                { value: "skip", labelId: "app.agent.start.skipUpload" },
            ]);
            ctx.uploadRequested = answer === "upload";
        }
        return { status: "done" };
    },
};
